// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, FormGroup, InputLabel, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseDatePicker,
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  ChangeValueModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  POUND,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import { IBaseOption, IFilterValueChange, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  IChangeStatusInvoiceBody,
  IFormChangeStatusInvoice,
  IInvoice,
  IQueryParamInvoice,
} from '~/utils/interface/invoice';
import { formatCurrency, formatDate, formatTitleCase } from '~/utils/helper/common';
import { StorageEnum } from '~/utils/enum/base';
import {
  DateFormatEnum,
  InvoiceStatusEnum,
  RoleEnum,
  SearchByFilterContentEnum,
  SearchByFilterEnum,
} from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { mockDataStatus, mockDataStatusInvoice } from '~/utils/mockData/invoice';
import { useAppDispatch } from '~/utils/redux/hooks';
import useDebounce from '~/utils/hooks/useDebounce';
import { changeStatusInvoice, getInvoiceList } from '~/thunks/invoice/invoiceThunk';
import { invoiceActions, selectIsRefreshInvoiceList } from '~/thunks/invoice/invoiceSlice';
import { schema } from './helper';
// Styles, images, icons
import styles from './CustomerInvoice.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onChangeStatus: (item: IInvoice) => void,
  onClickJobId: (id: string) => void
): IHeaderCellType<IInvoice>[] => {
  return [
    {
      id: 'id',
      label: t('table_head_title_invoice_id'),
      render: (record: IInvoice) => {
        return (
          <Box component={'div'} className={cx('viewId')}>
            {`${POUND}${record?.id || EMPTY_STRING}`}
          </Box>
        );
      },
      minWidth: 150,
    },
    {
      id: 'dueDate',
      label: t('table_head_title_due_date'),
      render: (record: IInvoice) =>
        record?.dueDate ? formatDate(record?.dueDate, DateFormatEnum.DD_MMM_YYYY) : EMPTY_STRING,
    },
    {
      id: 'total',
      label: t('table_head_title_amount'),
      render: (record: IInvoice) => formatCurrency(Number(record?.total || DEFAULT_NUMBER_ZERO)),
    },
    {
      id: 'status',
      label: t('table_head_title_status'),
      render: (record: IInvoice) => {
        return record?.status ? <BaseStatus label={record?.status} /> : EMPTY_STRING;
      },
    },
    {
      id: 'jobId',
      label: t('table_head_title_job_id'),
      render: (record: IInvoice) => {
        return (
          <Box
            component={'div'}
            className={cx('viewId')}
            onClick={(e) => {
              e.stopPropagation();
              onClickJobId(record?.jobId);
            }}
          >
            {record?.jobId ? `${POUND}${record?.jobId}` : EMPTY_STRING}
          </Box>
        );
      },
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: IInvoice) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            {...(record.status === InvoiceStatusEnum.OPEN && {
              onChangeStatus: () => onChangeStatus(record),
            })}
          />
        );
      },
    },
  ];
};

const CustomerInvoice = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: customerId } = useParams();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshInvoiceList = useSelector(selectIsRefreshInvoiceList);
  //#endregion Selector

  //#region Declare State
  const [filterParams, setFilterParams] = useState<IQueryParamInvoice>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryParamInvoice>({
    page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
    customerId: customerId,
  });
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [invoiceList, setInvoiceList] = useState<IInvoice[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isShowChangeStatusInvoice, setIsShowChangeStatusInvoice] = useState<boolean>(false);
  const [invoiceSelected, setInvoiceSelected] = useState<IInvoice | null>(null);

  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const { status, ...rest } = paramObject;

    const params = paramObject?.status === InvoiceStatusEnum.ALL ? rest : paramObject;

    handleGetInvoiceList(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: DEFAULT_LIMIT_PAGE,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!pagination) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (!isRefreshInvoiceList) return;

    handleGetInvoiceList(paramObject);
    dispatch(invoiceActions.setRefreshInvoiceList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshInvoiceList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetInvoiceList = (params: IQueryParamInvoice) => {
    setIsLoading(true);

    dispatch(getInvoiceList(params))
      .unwrap()
      .then((resp) => {
        const { responses, pagination } = resp.data;
        if (!responses) return;

        setInvoiceList(responses);
        setPagination(pagination);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleViewInvoice = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.invoice}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.invoice}/${id}`);
        break;
    }
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };

  const handleApplyFilter = (values: IQueryParamInvoice | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values?.status || values?.dueDate) {
      setParamObject({
        ...paramObject,
        ...values,
      });
    }
  };

  const handleFilterUnitChange = (
    option: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamInvoice>) => void
  ) => {
    if (!option) return;

    const { value } = option;

    onChange?.({ name: 'status', value: value });
  };

  const handleChangeDate = (
    date: string,
    onChange?: (value: IFilterValueChange<IQueryParamInvoice>) => void
  ) => {
    onChange?.({ name: 'dueDate', value: date });
  };

  const handleChangeStatus = (data: IInvoice) => {
    setIsShowChangeStatusInvoice(true);
    setInvoiceSelected(data);
  };

  const handleNavigateJobDetail = (jobId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.jobs}/${jobId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.jobs}/${jobId}`);
        break;
    }
  };

  const handleCloseChangeStatusInvoice = () => {
    setIsShowChangeStatusInvoice(false);
    setInvoiceSelected(null);
  };

  const handleSubmitChangeStatus = async (data: IFormChangeStatusInvoice) => {
    if (!invoiceSelected?.id) return;
    const payload: IChangeStatusInvoiceBody = {
      invoiceId: invoiceSelected?.id,
      body: data,
    };

    await dispatch(changeStatusInvoice(payload))
      .unwrap()
      .then((res) => {
        dispatch(invoiceActions.setRefreshInvoiceList(true));
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.invoice}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.invoice}/${id}`);
        break;
    }
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryParamBase | undefined,
    onChange?: (value: IFilterValueChange<IQueryParamBase>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryParamBase = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='customerInvoice' className={cx('container')}>
      <BaseToolbar>
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.ID}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.ID
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IQueryParamInvoice>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.JOB_ID}
                      checked={value?.fields?.includes(SearchByFilterEnum.JOB_ID) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('search_label_job_id')}
                />
              </FormGroup>

              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup className={cx('filterBy')}>
                <Box component={'div'} className={cx('filterGroup')}>
                  <InputLabel className={cx('label')}>{t('filter_label_status')}</InputLabel>

                  <BaseSelect
                    layout='horizontal'
                    width={'160px'}
                    options={mockDataStatus}
                    value={value?.status || InvoiceStatusEnum.ALL}
                    onChange={(option) => handleFilterUnitChange(option, onChange)}
                  />
                </Box>
                <Box component={'div'} className={cx('filterGroup')}>
                  <InputLabel className={cx('label')}>{t('filter_label_due_Date')}</InputLabel>

                  <BaseDatePicker
                    value={value?.dueDate}
                    onChange={(value) => handleChangeDate(value, onChange)}
                    width={'160px'}
                  />
                </Box>
              </FormGroup>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(t, handleViewInvoice, handleChangeStatus, handleNavigateJobDetail)}
          dataSource={invoiceList || []}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowPerPageChange}
          onRowClick={handleRowTableClick}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowChangeStatusInvoice && (
        <ChangeValueModal
          title={t('modal_change_status_title')}
          defaultValues={{
            status: invoiceSelected?.status || EMPTY_STRING,
          }}
          onSubmit={handleSubmitChangeStatus}
          onClose={handleCloseChangeStatusInvoice}
          schema={schema(t)}
          fields={[
            {
              name: 'status',
              label: t('form_change_status_invoice_label_status'),
              options: mockDataStatusInvoice,
              required: true,
            },
          ]}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default CustomerInvoice;
